import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { BillCreditGet } from "../../../data/Types"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"

import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import FloatingActionButton from "../components/FloatingActionButton"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import BillCreditTableRow from "../components/billTemplate/BillCreditTableRow"
import CreditDetailModal from "../components/credit/CreditDetailModal"
import { P } from "../../../auth/permissions"

export default function CreditPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.CREDIT_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "searchTerm"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm, LocalStorageKeys.CREDIT_FILTER + "searchTerm")
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "dateSince"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "dateTo"))
    const [filterCredit, setfilterCredit] = useState(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "credit"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.CREDIT_FILTER + "invertSort"))

    useResetPageOnFilterChange([searchParams, filterDateSince, filterDateTo, invertSort], () => handlePageChange(1))
    
    // Get Data Query (filterCredit is not supported by /credit/paginate, so it is neither sent nor part of the key)
    const queryKey = GeneralQueryKeys.CREDIT_DATA + page + searchParams + filterDateSince + filterDateTo + invertSort
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKey],
        queryFn: () =>
            getPaginatedData({
                pageType: "credit",
                page: page,
                limit: 15,
                search: searchParams,
                startDate: filterDateSince,
                endDate: filterDateTo,
                invertSort: invertSort,
            }),
    })

    const modalCon = useContext(ModalContext)
    const [selectedCredit, setselectedCredit] = useState<BillCreditGet>()
    const [showFilter, setshowFilter] = useState(false)

    const handleRemoveFilter = () => {
        setfilterDateTo("")
        setfilterDateSince("")
    }

    // FRONTEND LOGIC
    const displayedCredits = useMemo(() => data?.docs, [data?.docs])
    const creditTableColumns: ColumnType[] = [
        {
            label: "Erstellungsdatum",
            responsive: "hidden md:table-cell",
            width: "w-fit w-64",
        },
        {
            label: "Kunde",
            responsive: "table-cell",
            width: "w-fit",
        },
        {
            label: "Rechnung",
            width: "w-fit w-40",
        },
        {
            label: "Zahlungsanforderung",
            width: "w-fit w-40",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Gutschriftbetrag",
            width: "w-fit w-40",
            permissions: [P.buchhaltung_read],
        },
        {
            label: "Kommentar",
            width: "w-fit",
        },
    ]

    return (
        <BasePage>
            <HeadBanner title={"Gutschriften"} />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Kunde suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {/* {true && ( */}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps=" overflow-x-hidden my-4">
                        <BaseTable
                            columns={creditTableColumns}
                            tbody={displayedCredits?.map((item: BillCreditGet) => (
                                <BillCreditTableRow
                                    {...item}
                                    onClick={() => {
                                        setselectedCredit(item)
                                        modalCon?.trigger(1)
                                    }}
                                />
                            ))}
                        />
                        {(!displayedCredits || displayedCredits.length === 0) && <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erstellten Gutschriften</p>}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        searchCredit={filterCredit}
                        changeCredit={setfilterCredit}
                        searchdateSince={filterDateSince}
                        searchdateTo={filterDateTo}
                        changeDateTo={setfilterDateTo}
                        changeDateSince={setfilterDateSince}
                        removeFilter={handleRemoveFilter}
                        setVisible={() => setshowFilter(!showFilter)}
                        statusType={"credit"}
                        sessionStorageKey={LocalStorageKeys.CREDIT_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination pageSize={15} defaultCurrent={1} current={page} onChange={handlePageChange} total={data?.totalDocs} showSizeChanger={false} />
                    </div>
                </>
            )}
            <FloatingActionButton currentPage="credit" />
            <CreditDetailModal modalId={1} {...(selectedCredit as BillCreditGet)} />
        </BasePage>
    )
}
